import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CrtHierachyFilterNode, CrtHierachyFilterNodeInfo } from '../../providers/crt-hierachy-filter-helper';
import { ModalController } from '@ionic/angular';
import {
  IonHeader,
  IonFooter,
  IonTitle,
  IonButtons,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonToolbar,
  IonLabel,
  IonButton,
} from '@ionic/angular/standalone';
import { ObjetMetierFilter } from '../lr-ph/lr-ph-list.page';
import { FEATURE } from 'src/app/crt/feature';
import { CommonModule } from '@angular/common';
import { CrtTextTruncatedComponent } from 'src/app/crt/components/text-truncated/text-truncated.component';
import { CrtSliderSelecteurComponent } from 'src/app/crt/components/slider-selecteur/slider-selecteur.component';
import { CrtHierarchyFilterValuesPipe } from 'src/app/crt/pipes/hierarchy-filter.pipe';
import { ObjectFilterPage, ObjectFilterResult } from '../components/object-filter/object-filter';
import {
  CrtDataType,
  DataFilterTypeToDataTypes,
  DataTypeToDataFilterType,
  PRIORITE_LIBELLE,
} from 'src/app/crt/models/data';
import { EmplacementSite } from 'src/app/crt/models/emplacementSite';
import { CrtTypeFilterValuesPipe } from './type-filter-pipe';
import { NetworkProvider } from 'src/app/shared/providers/network/network';
import { Subject, from, switchMap, take, takeUntil } from 'rxjs';

export interface FilterSelectedLabel {
  fullLibelleInstallation: string[];
  libelleInstallation: string[];
  descriptionHierarNiv1: string[];
  descriptionHierarNiv2: string[];
  descriptionHierarNiv3: string[];
  descriptionMetier: string[];
  type: string[];
  priorite: string[];
}

type FilterLabels =
  | 'libelleInstallation'
  | 'descriptionHierarNiv1'
  | 'descriptionHierarNiv2'
  | 'descriptionHierarNiv3'
  | 'descriptionMetier'
  | 'type';

export type FilterType = 'LR_PH' | 'CRT_HISTORIC' | 'CRT_DATA' | 'LR_DATA' | 'SITE' | 'EVENTS';

export enum EventFilterRange {
  LAST_DAY = 0,
  LAST_WEEK = 1,
  LAST_MONTH = 2,
}

export enum EventSortEnum {
  DATE = 0,
  PRIORITE = 1,
}

export enum EtatFonctionnel {
  FONCTIONNEL = 'FONCTIONNEL',
  PARTIELLEMENT_FONCTIONNEL = 'PARTIELLEMENT_FONCTIONNEL',
  NON_FONCTIONNEL = 'NON_FONCTIONNEL',
}

export interface EtatFonctionnelFilter {
  fonctionnel: boolean;
  partiellementFonctionnel: boolean;
  nonFonctionnel: boolean;
}

type EtatFonctionnelKey = keyof EtatFonctionnelFilter;

const HIERAR_NIV = ['descriptionHierarNiv1', 'descriptionHierarNiv2', 'descriptionHierarNiv3'];
const DEFAULT_ETAT_FONCTIONNEL: EtatFonctionnelFilter = {
  fonctionnel: true,
  partiellementFonctionnel: true,
  nonFonctionnel: true,
};

@Component({
  selector: 'shared-filter',
  templateUrl: 'shared-filter.html',
  styleUrls: ['shared-filter.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    IonToolbar,
    IonFooter,
    IonHeader,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    CommonModule,
    CrtTextTruncatedComponent,
    CrtSliderSelecteurComponent,
    CrtHierarchyFilterValuesPipe,
    CrtTypeFilterValuesPipe,
  ],
})
export class CrtSharedFilterPage implements OnInit, OnDestroy {
  private readonly modalCtrl = inject(ModalController);
  private readonly network = inject(NetworkProvider);
  private readonly etatFonctionnelState = signal<EtatFonctionnelFilter>({ ...DEFAULT_ETAT_FONCTIONNEL });

  @Input()
  public isAffichageComplet: number;
  @Input()
  public emplacementSite: EmplacementSite;
  @Input()
  public filterTerms: ObjetMetierFilter;
  @Input()
  public title: string;
  @Input()
  public isFiltreHistorique = false;
  @Input()
  public type: FilterType;
  @Input()
  public etatBlocage: number;
  @Input()
  public sort: EventSortEnum;
  @Input()
  public status: number;
  @Input()
  public showDebutFinComm: number;
  @Input()
  public temporalWidth: EventFilterRange;
  @Input()
  public set etatFonctionnel(value: EtatFonctionnelFilter) {
    this.etatFonctionnelState.set(value ? { ...value } : { ...DEFAULT_ETAT_FONCTIONNEL });
  }

  public get etatFonctionnel(): EtatFonctionnelFilter {
    return this.etatFonctionnelState();
  }
  protected discordantOnly: number;
  protected etatDiscordant: number;
  protected readonly discordanceOptions: string[] = [$localize`Tous`, $localize`Discordants`];
  protected readonly siteDiscordanceOptions: string[] = [
    $localize`Tous`,
    $localize`Concordants`,
    $localize`Discordants`,
  ];
  protected readonly typeOptions = [$localize`Tous`, $localize`LR`, $localize`PH`];
  protected readonly modeAffichageOptions = [$localize`Simplifié`, $localize`Complet`];
  protected readonly blocageSiteOptions = [
    $localize`Tous`,
    $localize`Blocage Données`,
    $localize`Blocage Site en cours`,
  ];
  protected readonly blocageOptions = [$localize`Tous`, $localize`Débloquées`, $localize`Bloquées`];
  protected readonly etatOptions = [$localize`Tous`, $localize`Actifs`];
  protected readonly profondeursOptions = [
    $localize`24 heures glissantes`,
    $localize`7 jours glissants`,
    $localize`1 mois glissant`,
  ];
  protected readonly debutFinCommOptions = [$localize`Afficher`, $localize`Masquer`];
  protected readonly sortOptions = [$localize`Date`, $localize`Priorité`];
  protected readonly etatFonctionnelOptions = [
    { label: $localize`Fonctionnel`, key: 'fonctionnel' as EtatFonctionnelKey },
    { label: $localize`Partiellement fonctionnel`, key: 'partiellementFonctionnel' as EtatFonctionnelKey },
    { label: $localize`Non fonctionnel`, key: 'nonFonctionnel' as EtatFonctionnelKey },
  ];
  protected isConnected = signal(false);
  protected typeValue: number;
  private destroyed = new Subject<void>();
  public selectedLabels = signal<FilterSelectedLabel>(undefined);
  private hierarchyFilterNodes = signal<CrtHierachyFilterNode>(undefined);
  protected readonly enableLrPhFilters: boolean = FEATURE.ENABLE_LR_PH_FILTERS;
  protected libellesInstallationsFilterValues = computed(() => {
    return Object.keys(this.hierarchyFilterNodes())
      .sort()
      .map((sS) => ({
        label: sS,
        checked: this.selectedLabels().libelleInstallation.includes(sS),
      }));
  });

  protected multiOmChevronDisplay = computed(() => {
    const ohSize = Object.keys(this.objetsMetiersFilterValues()).length;
    if (ohSize === 0) {
      return false;
    }
    if (ohSize === 1) {
      return this.objetsMetiersFilterValues()[0].subValues.length > 1;
    }
    return true;
  });

  protected priorityFilterValues = computed(() => {
    if (!this.selectedLabels().descriptionMetier) {
      return [];
    }

    const values = {};
    this.selectedLabels().libelleInstallation.forEach((installation) => {
      this.getGroupedOh().forEach((oH) => {
        this.selectedLabels().descriptionMetier.forEach((oM) => {
          const findSubProp = (sub: CrtHierachyFilterNode) => {
            if (sub) {
              if (sub[oH]) {
                findSubProp(sub[oH].sub);
              }
              if (sub[oM]) {
                const objMetier = sub[oM];
                if (objMetier?.sub) {
                  Object.keys(objMetier.sub).forEach((prop) => {
                    if (PRIORITE_LIBELLE[prop]) {
                      values[prop] = {
                        checked: objMetier.sub[prop].selected,
                        label: prop,
                      };
                    }
                  });
                }
              }
            }
          };

          Object.values(this.hierarchyFilterNodes()[installation].sub).forEach((v) => {
            findSubProp(v.sub);
          });
        });
      });
    });

    return Object.keys(values)
      .sort()
      .map((o) => values[o]);
  });

  private getGroupedOh() {
    return [
      ...this.selectedLabels().descriptionHierarNiv1,
      ...this.selectedLabels().descriptionHierarNiv2,
      ...this.selectedLabels().descriptionHierarNiv3,
    ];
  }

  protected typeFilterValues = computed(() => {
    if (!this.selectedLabels().descriptionMetier) {
      return [];
    }

    const values = {};
    this.selectedLabels().libelleInstallation.forEach((installation) => {
      this.getGroupedOh().forEach((oH) => {
        this.selectedLabels().descriptionMetier.forEach((oM) => {
          const findSubType = (sub: CrtHierachyFilterNode) => {
            if (sub) {
              if (sub[oH]) {
                findSubType(sub[oH].sub);
              }
              if (sub[oM]) {
                const objMetier = sub[oM];
                if (objMetier?.sub) {
                  Object.keys(objMetier.sub).forEach((type) => {
                    const filterType = DataTypeToDataFilterType(<CrtDataType>type);
                    if (filterType !== undefined) {
                      values[filterType] = {
                        checked: objMetier.sub[type].selected,
                        label: filterType,
                      };
                    }
                  });
                }
              }
            }
          };

          Object.values(this.hierarchyFilterNodes()[installation].sub).forEach((v) => {
            findSubType(v.sub);
          });
        });
      });
    });
    return Object.keys(values)
      .sort()
      .map((o) => values[o]);
  });

  protected objetsMetiersFilterValues = computed(() => {
    if (!this.getGroupedOh()) {
      return [];
    }
    const constructSubValues = (currentLevel: CrtHierachyFilterNode) => {
      return Object.entries(currentLevel).map(([key, value]) => ({
        label: key,
        checked: value.selected,
        prop: value.prop,
        subValues: value.sub ? constructSubValues(value.sub) : undefined,
      }));
    };

    const values = {};
    Object.values(this.hierarchyFilterNodes()).forEach((sS) => {
      if (sS.selected && sS.sub) {
        Object.entries(sS.sub).forEach(([oH, oHValue]) => {
          const sub = oHValue.sub ? constructSubValues(oHValue.sub) : undefined;
          if (values[oH]) {
            values[oH].subValues = [...values[oH].subValues, ...sub];
          } else {
            values[oH] = {
              label: oH,
              checked: oHValue.selected,
              prop: oHValue.prop,
              subValues: sub,
            };
          }
        });
      }
    });

    return Object.keys(values)
      .sort()
      .map((o) => values[o]);
  });

  protected readonly etatFonctionnelFilterValues = computed(() => {
    return this.etatFonctionnelOptions.map((option) => ({
      label: option.label,
      checked: this.etatFonctionnelState()[option.key],
    }));
  });

  protected readonly hasCheckedEtatFonctionnel = computed(() => {
    return this.etatFonctionnelFilterValues().some((o) => o.checked);
  });

  protected readonly checkedEtatFonctionnelCount = computed(() => {
    return this.etatFonctionnelFilterValues().filter((o) => o.checked).length;
  });

  protected readonly singleCheckedEtatFonctionnel = computed(() => {
    return this.etatFonctionnelFilterValues().find((o) => o.checked);
  });

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  ngOnInit(): void {
    this.isConnected.set(this.network.isConnected());
    this.network.networkStatusSubject.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.isConnected.set(this.network.isConnected());
    });
    this.hierarchyFilterNodes.set(this.filterTerms.hierarchyFilterNodes);
    this.discordantOnly = this.filterTerms.discordantOnly ? 1 : 0;
    this.etatDiscordant = this.filterTerms.etatDiscordant;
    this.typeValue = this.filterTerms.typeValue;
    this.updateSelectedLabels();
  }

  protected closeModal() {
    this.modalCtrl.dismiss();
  }

  protected applyFilter() {
    if (this.type === 'SITE') {
      this.modalCtrl.dismiss({
        etatDiscordant: this.etatDiscordant,
        etatBlocage: this.etatBlocage,
      });
    } else {
      const mappedType = [];
      this.selectedLabels().type.forEach((type) => {
        mappedType.push(...DataFilterTypeToDataTypes(type));
      });

      const hasEtatFonctionnelFilter =
        this.type === 'LR_PH' || this.type === 'CRT_HISTORIC' || this.type === 'CRT_DATA';

      this.modalCtrl.dismiss({
        libellesInstallationsFilterValues: this.selectedLabels()?.libelleInstallation,
        hierarchyFilterValues: this.getGroupedOh(),
        metiersFilterValues: this.selectedLabels()?.descriptionMetier,
        discordantOnly: this.discordantOnly === 1,
        hierarchyFilterNodes: this.hierarchyFilterNodes(),
        isAffichageComplet: this.isAffichageComplet === 1,
        etatBlocage: this.etatBlocage,
        temporalWidth: this.temporalWidth,
        sort: this.sort,
        status: this.status === 1,
        showDebutFinComm: this.showDebutFinComm === 1,
        filterTerms: {
          ...this.selectedLabels(),
          type: mappedType,
        },
        typeValue: this.typeValue,
        isFiltered: this.isFiltreActif(),
        ...(hasEtatFonctionnelFilter && { etatFonctionnel: this.etatFonctionnelState() }),
      });
    }
  }

  private findChildSelected(sub: CrtHierachyFilterNodeInfo) {
    return Object.values(sub.sub).some((s) => s.selected || this.findChildSelected(s));
  }

  /**
   * Met à jour les objets selectionnés
   */
  private updateSelectedLabels() {
    const newSelected: FilterSelectedLabel = {
      fullLibelleInstallation: [],
      libelleInstallation: [],
      descriptionHierarNiv1: [],
      descriptionHierarNiv2: [],
      descriptionHierarNiv3: [],
      descriptionMetier: [],
      type: [],
      priorite: [],
    };

    const fetchSubValues = (
      currentLevel: CrtHierachyFilterNodeInfo & {
        key: string;
      }
    ) => {
      if (currentLevel.selected || (this.findChildSelected(currentLevel) && HIERAR_NIV.includes(currentLevel.prop))) {
        switch (currentLevel.prop) {
          case 'type':
            const filterType = DataTypeToDataFilterType(<CrtDataType>currentLevel.key);
            if (filterType) {
              newSelected.type.push(filterType);
            }
            break;
          case 'descriptionMetier':
            newSelected.descriptionMetier.push(currentLevel.key);
            if (currentLevel.sub) {
              Object.keys(currentLevel.sub).forEach((prop) => {
                if (currentLevel.sub[prop].selected && PRIORITE_LIBELLE[prop]) {
                  newSelected.priorite.push(prop);
                }
              });
            }
            break;
          case 'descriptionHierarNiv1':
            newSelected.descriptionHierarNiv1.push(currentLevel.key);
            break;
          case 'descriptionHierarNiv2':
            newSelected.descriptionHierarNiv2.push(currentLevel.key);
            break;
          case 'descriptionHierarNiv3':
            newSelected.descriptionHierarNiv3.push(currentLevel.key);
            break;
        }

        if (currentLevel.sub) {
          Object.entries(currentLevel.sub).forEach(([subKey, subValue]) => {
            fetchSubValues({
              ...subValue,
              key: subKey,
            });
          });
        }
      }
    };

    if (this.hierarchyFilterNodes()) {
      Object.entries(this.hierarchyFilterNodes()).forEach(([sSKey, sSValue]) => {
        if (sSValue.selected) {
          newSelected.libelleInstallation.push(sSKey);
        }
        fetchSubValues({
          key: sSKey,
          ...sSValue,
        });
      });
      newSelected.type = Array.from(new Set(newSelected.type));
      newSelected.priorite = Array.from(new Set(newSelected.priorite));
      this.selectedLabels.update((current) => ({
        ...current,
        ...newSelected,
      }));
      if (this.selectedLabels().libelleInstallation) {
        this.selectedLabels.update((current) => ({
          ...current,
          fullLibelleInstallation: this.selectedLabels().libelleInstallation.map((label) => {
            const dkCodeArray = this.emplacementSite.installations.filter((install) => {
              return label === install.libelle;
            });
            let dkCode = '';
            if (dkCodeArray.length === 1) {
              dkCode = dkCodeArray[0].dkCode + ' - ';
            }
            return dkCode + label;
          }),
        }));
      }
    }
  }

  public async openPriorityFilter() {
    if (!this.selectedLabels().descriptionMetier) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: ObjectFilterPage,
      componentProps: {
        filterValues: this.priorityFilterValues(),
        title: `Priorité`,
      },
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data) {
      data.filterValues.forEach((prop) => {
        this.selectedLabels().libelleInstallation.forEach((installation) => {
          this.getGroupedOh().forEach((oH) => {
            this.selectedLabels().descriptionMetier.forEach((oM) => {
              this.hierarchyFilterNodes.update((hierarchy) => {
                Object.entries(hierarchy).forEach(([k, v]) => {
                  const finds = this.findAllTargetNodes(v, k, 'descriptionMetier');
                  finds.forEach((find) => {
                    if (find.sub && find.sub[prop.label]) {
                      find.sub[prop.label].selected = prop.checked;
                    }
                  });
                });
                return hierarchy;
              });
            });
          });
        });
      });
      this.updateSelectedLabels();
    }
  }

  private findAllTargetNodes(
    node: CrtHierachyFilterNodeInfo,
    key: string,
    target: FilterLabels
  ): CrtHierachyFilterNodeInfo[] {
    let nodes = [];

    if (this.selectedLabels()[node.prop]?.includes(key)) {
      if (target === node.prop) {
        nodes.push(node);
      }

      // Collecte récursive des nœuds correspondants dans les sous-nœuds
      for (const [k, v] of Object.entries(node.sub)) {
        nodes = nodes.concat(this.findAllTargetNodes(v, k, target));
      }
    }

    return nodes;
  }

  public async openTypeFilter() {
    if (!this.selectedLabels().descriptionMetier) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: ObjectFilterPage,
      componentProps: {
        filterValues: this.typeFilterValues(),
        title: $localize`Type`,
      },
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();

    if (data) {
      data.filterValues.forEach((type) => {
        const types = DataFilterTypeToDataTypes(type.label);
        types.forEach((t) => {
          this.hierarchyFilterNodes.update((hierarchy) => {
            Object.entries(hierarchy).forEach(([k, v]) => {
              const finds = this.findAllTargetNodes(v, k, 'descriptionMetier');
              finds.forEach((find) => {
                if (find.sub && find.sub[t]) {
                  find.sub[t].selected = type.checked;
                }
              });
            });
            return hierarchy;
          });
        });
      });
      this.updateSelectedLabels();
    }
  }

  public openEtatFonctionnelFilter() {
    from(
      this.modalCtrl.create({
        component: ObjectFilterPage,
        componentProps: {
          filterValues: this.etatFonctionnelFilterValues(),
          title: this.type === 'LR_PH' ? $localize`Etat de l'objet` : $localize`Etat de la donnée`,
        },
      })
    )
      .pipe(
        switchMap((modal) => from(modal.present()).pipe(switchMap(() => from(modal.onDidDismiss())))),
        take(1),
        takeUntil(this.destroyed)
      )
      .subscribe(({ data }) => {
        if (!data) {
          return;
        }

        const nextEtatFonctionnel: EtatFonctionnelFilter = {
          fonctionnel: false,
          partiellementFonctionnel: false,
          nonFonctionnel: false,
        };
        data.filterValues.forEach((option: { label: string; checked: boolean }) => {
          const matchedOption = this.etatFonctionnelOptions.find((o) => o.label === option.label);
          if (matchedOption && option.checked) {
            nextEtatFonctionnel[matchedOption.key] = true;
          }
        });
        this.etatFonctionnelState.set(nextEtatFonctionnel);
      });
  }

  public async openLibellesInstallationsFilter() {
    const modal = await this.modalCtrl.create({
      component: ObjectFilterPage,
      componentProps: {
        filterValues: this.libellesInstallationsFilterValues(),
        title: $localize`Libelles Installations`,
      },
    });
    modal.onDidDismiss().then((modalData) => {
      if (modalData && modalData.data) {
        const libellesInstallations = modalData.data;
        libellesInstallations.filterValues.forEach((sS: { label: string; checked: boolean }) => {
          this.hierarchyFilterNodes.update((h) => {
            h[sS.label] = {
              ...h[sS.label],
              selected: sS.checked,
            };
            return h;
          });
        });
        this.updateSelectedLabels();
      }
    });
    await modal.present();
  }

  public async openBusinessObjectFilter() {
    const modal = await this.modalCtrl.create({
      component: ObjectFilterPage,
      componentProps: {
        filterValues: this.objetsMetiersFilterValues(),
        title: $localize`Sélection OH / OM`,
      },
    });
    modal.onDidDismiss().then((modalData) => {
      if (modalData && modalData.data) {
        const objetsMetiers = modalData.data as ObjectFilterResult;
        objetsMetiers.filterValues.forEach((oH) => {
          this.selectedLabels().libelleInstallation.forEach((sS) => {
            this.hierarchyFilterNodes.update((nodes) => {
              const h = nodes[sS].sub[oH.label];
              if (h) {
                h.selected = oH.checked;
                h.sub = this.processSubValues(oH.subValues.filter((s) => Object.keys(h.sub).includes(s.label)));
              }
              return nodes;
            });
          });
        });
      }
      this.updateSelectedLabels();
    });
    await modal.present();
  }

  private processSubValues(subValues: any[]): any {
    if (!subValues) {
      return {};
    }

    return subValues.reduce((acc, sub) => {
      const types = sub.subValues ? this.processSubValues(sub.subValues) : {};

      acc[sub.label] = {
        prop: sub.prop,
        selected: sub.checked,
        sub: types,
      };

      return acc;
    }, {});
  }

  private isFiltreActif(): boolean {
    let oMcount = 0;
    this.objetsMetiersFilterValues().forEach((oh) => {
      oMcount += oh.subValues?.length;
    });

    const etatFonctionnel = this.etatFonctionnelState();
    const hasEtatFonctionnelFilter = this.type === 'LR_PH' || this.type === 'CRT_HISTORIC' || this.type === 'CRT_DATA';
    const etatFonctionnelFiltered =
      hasEtatFonctionnelFilter &&
      (!etatFonctionnel.fonctionnel || !etatFonctionnel.partiellementFonctionnel || !etatFonctionnel.nonFonctionnel);

    return (
      this.selectedLabels().priorite.length !== this.priorityFilterValues().length ||
      this.selectedLabels().libelleInstallation.length !== this.libellesInstallationsFilterValues().length ||
      this.selectedLabels().descriptionMetier.length !== oMcount ||
      this.selectedLabels().type.length !== this.typeFilterValues().length ||
      (this.type === 'EVENTS' ? this.status !== 1 : this.status === 1) ||
      this.discordantOnly === 1 ||
      (this.typeValue !== undefined ? this.typeValue !== 0 : false) ||
      (this.etatBlocage !== undefined ? this.etatBlocage !== 0 : false) ||
      this.isAffichageComplet === 1 ||
      etatFonctionnelFiltered
    );
  }
}
